import React, { useMemo } from 'react';
import { TextField, Box, Chip, Checkbox, Typography, MenuItem } from '@mui/material';
import Autocomplete, {
	AutocompleteChangeDetails,
	AutocompleteChangeReason,
	createFilterOptions,
} from '@mui/material/Autocomplete';
import { AutocompleteGetTagProps } from '@mui/material/useAutocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'services/localization/useTranslation';
import { uniqBy } from 'lodash';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface BaseOption {
	id: string;
	label: string;
}

interface Option extends BaseOption {
	groupLabel?: string;
}

interface GroupedOption {
	name: string;
	options: BaseOption[];
}

interface Props {
	value: string[] | undefined;
	options: (GroupedOption | BaseOption)[];
	onChange: (value: string[]) => void;
	chipLimit?: number;
}

const isGroupedOption = (option: GroupedOption | BaseOption): option is GroupedOption => {
	return !!(option as GroupedOption).name;
};

const customFilter = createFilterOptions({
	matchFrom: 'start',
	stringify: (option: Option) => option.label,
});

const ItemSelect = (props: Props) => {
	const { value = [], onChange, options: _options, chipLimit } = props;
	const { t } = useTranslation();

	const options: Option[] = useMemo(
		() =>
			_options.flatMap((option) => {
				if (isGroupedOption(option)) {
					return option.options.map((opt) => ({
						id: opt.id,
						label: opt.label,
						groupLabel: option.name,
					}));
				}
				return option;
			}),
		[_options],
	);

	const valueOptions = options.filter((opt) => value.indexOf(opt.id) !== -1);

	const handleChange = (
		_: any,
		selectedOptions: Option[],
		type: AutocompleteChangeReason,
		value?: AutocompleteChangeDetails<Option>,
	) => {
		// Categories can include same product IDs, so we need to expicitly remove all values with same ID to handle duplicates correctly
		const options =
			type === 'removeOption'
				? selectedOptions.filter((opt) => opt.id !== value?.option.id)
				: selectedOptions;
		onChange(options.map((option) => option.id));
	};

	const renderOption = (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: Option,
		{ selected }: { selected: boolean },
	) => {
		return (
			<MenuItem key={option.id} {...props}>
				<Checkbox
					icon={icon}
					color="primary"
					checkedIcon={checkedIcon}
					style={{ marginRight: 8 }}
					checked={selected}
				/>
				<Typography variant="body1">{option.label}</Typography>
			</MenuItem>
		);
	};

	const renderTags = (value: Option[], getTagProps: AutocompleteGetTagProps) => {
		const uniqueValues = uniqBy(value, 'id');
		return uniqueValues.map((option: Option, index: number) => {
			if (!chipLimit || index < chipLimit) {
				return (
					<Box key={index} mr={0.2} mb={0.4} maxWidth="100%">
						<Chip label={option.label} {...getTagProps({ index: index as number })} />
					</Box>
				);
			}
			if (index === chipLimit) {
				return `+ ${uniqueValues.length - chipLimit} ${t('common:more', 'More').toLowerCase()}`;
			}
			return null;
		});
	};

	return (
		<Autocomplete
			autoComplete
			autoHighlight
			multiple
			disableCloseOnSelect
			id="item-select"
			options={options}
			groupBy={(option) => option.groupLabel ?? ''}
			getOptionLabel={(option) => option.label}
			value={valueOptions}
			onChange={handleChange}
			filterOptions={customFilter}
			renderOption={renderOption}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={value.length === 0 ? t('common:actions.search') : ''}
					fullWidth
					margin="normal"
					variant="outlined"
				/>
			)}
			renderTags={renderTags}
			noOptionsText={
				<Typography variant="body2">
					{t('product.categorySelect.noOptions', 'No results')}
				</Typography>
			}
		/>
	);
};

export default ItemSelect;
